import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import Table from '@mui/material/Table/Table';
import TableHead from '@mui/material/TableHead/TableHead';
import TableBody from '@mui/material/TableBody/TableBody';
import TableRow from '@mui/material/TableRow/TableRow';
import TableCell from '@mui/material/TableCell/TableCell';
import TableContainer from '@mui/material/TableContainer/TableContainer';
import Paper from '@mui/material/Paper/Paper';
import Chip from '@mui/material/Chip/Chip';
import Button from '@mui/material/Button/Button';
import TextField from '@mui/material/TextField/TextField';
import Alert from '@mui/material/Alert/Alert';
import Avatar from '@mui/material/Avatar/Avatar';
import Dialog from '@mui/material/Dialog/Dialog';
import DialogTitle from '@mui/material/DialogTitle/DialogTitle';
import DialogContent from '@mui/material/DialogContent/DialogContent';
import DialogContentText from '@mui/material/DialogContentText/DialogContentText';
import DialogActions from '@mui/material/DialogActions/DialogActions';
import BusinessIcon from '@mui/icons-material/Business';
import {
    createOrganization,
    deleteOrganization,
    listAllOrganizations,
    OrganizationDetail
} from '../../services/organization.service';
import { refreshMyOrganizations } from '../../hooks/useMyOrganizations';

export const AdminOrganizations = () => {
    const [organizations, setOrganizations] = useState<OrganizationDetail[]>([]);
    const [name, setName] = useState('');
    const [domains, setDomains] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [error, setError] = useState('');
    const [creating, setCreating] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<OrganizationDetail | null>(null);

    const load = useCallback(() => {
        listAllOrganizations().then(setOrganizations, (err) => {
            console.error(err);
            setError('Could not load organizations');
        });
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const create = () => {
        setError('');
        setCreating(true);
        createOrganization({
            name,
            domains: domains.split(',').map(d => d.trim()).filter(Boolean),
            adminEmail: adminEmail.trim() || undefined
        }).then(() => {
            setName('');
            setDomains('');
            setAdminEmail('');
            load();
            refreshMyOrganizations();
        }, (err) => {
            setError(err.response?.data?.message || 'Could not create organization');
        }).finally(() => {
            setCreating(false);
        });
    }

    const doDelete = () => {
        if (!confirmDelete) {
            return;
        }
        deleteOrganization(confirmDelete._id).then(() => {
            setConfirmDelete(null);
            load();
            refreshMyOrganizations();
        }, (err) => {
            setError(err.response?.data?.message || 'Could not delete organization');
            setConfirmDelete(null);
        });
    }

    return (
        <Stack spacing={2}>
            <div>
                <Typography variant="h4" component="h2">Organizations</Typography>
                <Typography variant="body2" color="text.secondary">
                    Organizations own branding that their hackathons inherit. Users whose email matches a domain can link hackathons to the organization.
                </Typography>
            </div>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
                <TextField size="small" label="Name" value={name} onChange={e => setName(e.target.value)} />
                <TextField size="small" label="Domains" placeholder="acme.com, acme.io" value={domains} onChange={e => setDomains(e.target.value)} />
                <TextField size="small" label="First admin email (optional)" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} sx={{ minWidth: 240 }} />
                <Button variant="contained" disabled={!name.trim() || creating} onClick={create}>Create</Button>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Organization</TableCell>
                            <TableCell>Domains</TableCell>
                            <TableCell>Admins</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            organizations.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No organizations yet</Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        }
                        {
                            organizations.map(org => (
                                <TableRow key={org._id} hover>
                                    <TableCell>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Avatar variant="rounded" src={org.branding?.logoUrl} sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: org.branding?.primaryColor || 'primary.main'
                                            }}>
                                                <BusinessIcon fontSize="small" />
                                            </Avatar>
                                            <Link to={`/org/${org.slug}`}>{org.name}</Link>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                            {org.domains.map(d => <Chip key={d} size="small" label={d} />)}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{org.admins.map(a => a.email).join(', ') || '—'}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" component={Link} to={`/org/${org.slug}`}>Settings</Button>
                                        <Button size="small" color="error" onClick={() => setConfirmDelete(org)}>Delete</Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
                <DialogTitle>Delete {confirmDelete?.name}?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Hackathons linked to this organization will be unlinked and lose its branding. Their own branding overrides are kept.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={doDelete}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}
